import { Box, Theme, Typography } from '@mui/material';
import { type SerializedError } from '@reduxjs/toolkit';
import { type FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { useIntl } from 'react-intl';

import { Icon, LoadingSpinner, WorkflowMessage } from '@verantos-dev/shared-components';
import {
  CohortCharacteristicsResultType,
  CohortDefinitionStatus,
  CohortSummaryAttributeResult,
  CohortSummaryDetails,
} from '@verantos-dev/types-cohorts';

import { BarChart } from './components/BarChart';
import { ChartLayout } from './components/ChartLayout';
import { CohortCharacteristicsDataDialog } from './components/CohortCharacteristicsDataDialog';
import { PieChart } from './components/PieChart';
import { useCohortCharacteristics } from './hooks/useCohortCharacteristics';

export interface CohortCharacteristicsProps {
  projectId: string;
  cohortId: string;
  isFinalFetchTimerActive: boolean;
}

export const CohortCharacteristics = ({
  projectId,
  cohortId,
  isFinalFetchTimerActive,
}: CohortCharacteristicsProps) => {
  const {
    theme,

    latestCohortStatus,
    characteristicsError,
    isCohortSummaryInitiallyLoading,

    ethnicityData,
    genderData,
    raceData,
    ageAtIndexData,

    displayDataDialog,
    onHideDataDialog,
    onShowDataDialog,
    totalSubjectCount,
    cohortSummaryData,
    hasSummaryData,
    cohortMessages,
  } = useCohortCharacteristics({ projectId, cohortId });

  return (
    <Box
      gap={theme.spacing(4)}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        justifyContent: 'center',
        width: '100%',
        minWidth: theme.breakpoints.values.md,
      }}
    >
      {hasSummaryData && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
          }}
        >
          <>
            <Typography variant="h5">{cohortMessages.title}</Typography>

            <Box
              id="cohort-characteristics-open-tabular-data"
              onClick={onShowDataDialog}
              sx={{
                cursor: 'pointer',
                borderRadius: '4px',
                padding: theme.spacing(1),
                height: 32,
                '&:hover': { backgroundColor: `${theme.palette.action.focus} !important` },
              }}
            >
              <Icon id="cohort-characteristics-open-chart" icon="showData" width={24} height={24} />
            </Box>
          </>
        </Box>
      )}

      <CohortCharacteristicsContent
        isFinalFetchTimerActive={isFinalFetchTimerActive}
        isCohortSummaryInitiallyLoading={isCohortSummaryInitiallyLoading}
        characteristicsError={characteristicsError}
        latestCohortStatus={latestCohortStatus}
        ageAtIndexData={ageAtIndexData}
        ethnicityData={ethnicityData}
        genderData={genderData}
        raceData={raceData}
        cohortSummaryData={cohortSummaryData}
        hasSummaryData={hasSummaryData}
        theme={theme}
        cohortMessages={cohortMessages}
      />

      {displayDataDialog && cohortSummaryData && (
        <CohortCharacteristicsDataDialog
          totalSubjectCount={totalSubjectCount}
          onHideDataDialog={onHideDataDialog}
          cohortSummaryData={cohortSummaryData}
        />
      )}
    </Box>
  );
};

interface CohortCharacteristicsContentProps {
  theme: Theme;
  latestCohortStatus: CohortDefinitionStatus | undefined;
  isCohortSummaryInitiallyLoading: boolean;
  characteristicsError: FetchBaseQueryError | SerializedError | undefined;
  ethnicityData: CohortSummaryAttributeResult[] | undefined;
  genderData: CohortSummaryAttributeResult[] | undefined;
  raceData: CohortSummaryAttributeResult[] | undefined;
  ageAtIndexData: CohortSummaryAttributeResult[] | undefined;
  cohortSummaryData: CohortSummaryDetails | undefined;
  hasSummaryData: boolean;
  cohortMessages: Record<string, string>;
  isFinalFetchTimerActive: boolean;
}

export const CohortCharacteristicsContent = ({
  theme,
  latestCohortStatus,
  isCohortSummaryInitiallyLoading,
  characteristicsError,
  ethnicityData,
  genderData,
  raceData,
  ageAtIndexData,
  cohortSummaryData,
  cohortMessages,
  isFinalFetchTimerActive,
}: CohortCharacteristicsContentProps) => {
  if (isCohortSummaryInitiallyLoading) {
    return (
      <Box
        data-testid={'cohortCharacteristicsLoading'}
        sx={{
          borderRadius: '4px',
          boxShadow:
            '0px 1px 8px 0px rgba(0, 0, 0, 0.12), 0px 3px 4px 0px rgba(0, 0, 0, 0.14), 0px 3px 3px -2px rgba(0, 0, 0, 0.20)',
          padding: theme.spacing(6),
          width: '100%',
          height: '348px',
        }}
      >
        <LoadingSpinner
          id="characteristicsLoadingSpinner"
          data-testid="characteristicsLoadingSpinner"
          title={cohortMessages.loadingTitle}
        />
      </Box>
    );
  }

  if (latestCohortStatus === CohortDefinitionStatus.DRAFT) {
    return (
      <WorkflowMessage
        id={'CohortCharacteristics-draft'}
        title={cohortMessages.draftTitle}
        message={cohortMessages.draftMessage}
        sx={{ width: '100%', height: '348px' }}
      />
    );
  }

  if (
    (latestCohortStatus === CohortDefinitionStatus.PROCESSING || isFinalFetchTimerActive) &&
    (!cohortSummaryData ||
      (!cohortSummaryData.ageAtIndex &&
        !cohortSummaryData.gender &&
        !cohortSummaryData.ethnicity &&
        !cohortSummaryData.race))
  ) {
    return (
      <WorkflowMessage
        id={'CohortCharacteristics-processing'}
        title={cohortMessages.processingTitle}
        message={cohortMessages.processingMessage}
        sx={{ width: '100%', height: '348px' }}
      />
    );
  }

  if (
    !cohortSummaryData ||
    (!cohortSummaryData.ageAtIndex &&
      !cohortSummaryData.ethnicity &&
      !cohortSummaryData.gender &&
      !cohortSummaryData.race)
  ) {
    return (
      <WorkflowMessage
        id={'CohortCharacteristics-processed-no-results'}
        title={cohortMessages.errorTitle}
        message={cohortMessages.errorMessage}
        sx={{ width: '100%', height: '348px' }}
      />
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      <ChartLayout
        chartType={CohortCharacteristicsResultType.AGE_AT_INDEX}
        heading="Age at index"
        isEmpty={!ageAtIndexData?.length}
        skipHeight
        sx={{ mb: theme.spacing(6) }}
      >
        <BarChart data={ageAtIndexData} resultType={CohortCharacteristicsResultType.AGE_AT_INDEX} />
      </ChartLayout>

      <Box
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: theme.spacing(6),
        }}
      >
        <ChartLayout
          chartType={CohortCharacteristicsResultType.GENDER}
          heading="Gender"
          isEmpty={!genderData?.length}
        >
          <PieChart data={genderData} resultType={CohortCharacteristicsResultType.GENDER} />
        </ChartLayout>

        <ChartLayout
          chartType={CohortCharacteristicsResultType.RACE}
          heading="Race"
          isEmpty={!raceData?.length}
        >
          <PieChart data={raceData} resultType={CohortCharacteristicsResultType.RACE} />
        </ChartLayout>

        <ChartLayout
          chartType={CohortCharacteristicsResultType.ETHNICITY}
          heading="Ethnicity"
          isEmpty={!ethnicityData?.length}
        >
          <PieChart data={ethnicityData} resultType={CohortCharacteristicsResultType.ETHNICITY} />
        </ChartLayout>
      </Box>
    </Box>
  );
};
